/**
 * Ce qui manque à ma fiche pour qu'un voisin ait envie de me parler.
 *
 * POURQUOI CE FICHIER, dans les mots du commanditaire :
 *
 *   « Plus on a accès aux modules Cabaza, plus la page de l'utilisateur
 *     s'enrichit. Il doit donc acheter des modules et/ou des abonnements pour
 *     que sa fiche soit de plus en plus intéressante pour le voisinage —
 *     l'effet viral et le concept de Cabaza. »
 *
 * Une fiche vide n'intéresse personne, et personne ne sait qu'elle est vide :
 * son auteur la voit remplie de ce qu'il connaît déjà de lui-même. Ce calcul
 * lui montre ce qu'un VISITEUR y trouve.
 *
 * IL NE COMPTE PAS LES MODULES POSSÉDÉS, MAIS CEUX QUI PORTENT DU CONTENU.
 * Acheter un module ne remplit pas une fiche — c'est la distinction que fait
 * déjà `ongletsProfil.js` pour décider d'afficher un onglet, et elle vaut ici
 * pour la même raison : un visiteur se moque de ce qu'on possède.
 *
 * IL NE JUGE PAS. Pas de score sur cent, pas de « profil incomplet » : une
 * personne qui choisit de ne rien exposer fait un choix légitime, et le lui
 * reprocher serait malvenu sur un réseau qui promet la maîtrise de ses
 * données. On montre ce qui existe, on suggère ce qui pourrait exister.
 */

/**
 * Ce qu'une fiche peut porter, dans l'ordre où cela compte pour un voisin.
 *
 * L'ordre n'est pas décoratif : savoir ce que quelqu'un fait précède de loin
 * savoir quels livres il prête.
 */
export const APPORTS = [
    { cle: 'photo', moduleCode: null, libelle: 'Une photo' },
    { cle: 'occupations', moduleCode: null, libelle: 'Vos métiers' },
    { cle: 'description', moduleCode: null, libelle: 'Une présentation' },
    { cle: 'MOMENTS', moduleCode: 'MOMENTS', libelle: 'Vos moments' },
    { cle: 'OFFERS', moduleCode: 'OFFERS', libelle: 'Ce que vous proposez' },
    { cle: 'WORK', moduleCode: 'WORK', libelle: 'Ce que vous cherchez' },
    { cle: 'BOOKS', moduleCode: 'BOOKS', libelle: 'Vos livres à partager' },
    { cle: 'PRODUCTS', moduleCode: 'PRODUCTS', libelle: 'Vos produits' },
    { cle: 'WEBSITES', moduleCode: 'WEBSITES', libelle: 'Vos sites web' },
    { cle: 'ADVERTISEMENTS', moduleCode: 'ADVERTISEMENTS', libelle: 'Vos annonces' },
    { cle: 'KNOWLEDGE', moduleCode: 'KNOWLEDGE', libelle: 'Vos connaissances' }
];

/**
 * L'état d'enrichissement d'une fiche.
 *
 * @param {Object} profil    la fiche, telle que l'API la rend
 * @param {Set|Array} modules les codes de modules accessibles
 * @param {Object} contenus  { CODE: nombre } — ce qui est réellement rempli
 * @returns {{remplis: Array, manquants: Array, aAcquerir: Array, total: number}}
 *
 * - `remplis`    ce qu'un visiteur trouve déjà
 * - `manquants`  ce qui pourrait être rempli, module en main
 * - `aAcquerir`  ce qui demanderait un module supplémentaire
 */
export function etatEnrichissement(profil, modules, contenus = {}) {
    const codes = modules instanceof Set ? modules : new Set(modules || []);
    const comptes = contenus || {};

    const remplis = [];
    const manquants = [];
    const aAcquerir = [];

    APPORTS.forEach((apport) => {
        // Les trois premiers n'appartiennent à aucun module : ils sont
        // toujours à la portée de tout le monde.
        if (!apport.moduleCode) {
            if (apportDeBaseRempli(profil, apport.cle)) remplis.push(apport);
            else manquants.push(apport);
            return;
        }

        const nombre = Number(comptes[apport.moduleCode]) || 0;

        if (nombre > 0) {
            remplis.push({ ...apport, nombre });
        } else if (codes.has(apport.moduleCode)) {
            // Le module est là, la place est vide : c'est le cas le plus
            // utile à signaler — rien à acheter, tout à écrire.
            manquants.push(apport);
        } else {
            aAcquerir.push(apport);
        }
    });

    return { remplis, manquants, aAcquerir, total: APPORTS.length };
}

/**
 * Les trois apports qui ne dépendent d'aucun module.
 *
 * Lit les trois casses : ColdFusion rend ses colonnes en minuscules, d'autres
 * routes ont rendu du camelCase. Une casse oubliée ne lève aucune erreur —
 * le champ vaut `undefined`, et la fiche paraît vide alors qu'elle ne l'est
 * pas.
 */
function apportDeBaseRempli(profil, cle) {
    if (!profil) return false;

    const lire = (...noms) => {
        for (const n of noms) {
            const v = profil[n];
            if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
        }
        return '';
    };

    if (cle === 'photo') {
        return Boolean(lire('photo', 'PHOTO', 'profilePhoto', 'profilephoto'));
    }
    if (cle === 'description') {
        return Boolean(lire('description', 'DESCRIPTION'));
    }
    if (cle === 'occupations') {
        // Le métier vient d'une autre table ; la fiche porte au moins un
        // intitulé quand il existe.
        return Boolean(lire('occupationName_fr', 'occupationname_fr', 'profession', 'PROFESSION'));
    }
    return false;
}

/**
 * Le prochain geste utile, ou null s'il n'y en a pas.
 *
 * UN SEUL À LA FOIS, délibérément. Une liste de dix manques décourage ;
 * une suggestion se suit. On propose d'abord ce qui ne coûte rien — remplir
 * un module qu'on possède déjà — avant ce qui demande un achat.
 *
 * @param {Object} etat le résultat de `etatEnrichissement`
 * @returns {Object|null}
 */
export function prochainGeste(etat) {
    if (!etat) return null;
    if (etat.manquants?.length) return { ...etat.manquants[0], type: 'remplir' };
    if (etat.aAcquerir?.length) return { ...etat.aAcquerir[0], type: 'acquerir' };
    return null;
}
